import { useMemo, useState } from "react";

import { useFieldArray, useForm } from "react-hook-form";

import dayjs from "dayjs";
import { Lock, Plus, Trash2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "~/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Textarea } from "~/components/ui/textarea";
import { ALIACE_SPA_ID } from "~/constants/companies";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";
import { useTodayExchangeRate } from "~/hooks/exchangeRates";
import {
  useActiveProducts,
  useActiveSuppliers,
  useMiscDescriptions,
  useOilTypes,
  useRawMaterials,
} from "~/hooks/purchasing";
import {
  CURRENCY_LABELS,
  CURRENCY_SYMBOLS,
  DISCOUNT_TYPE_LABELS,
  formatIlaRate,
  ILA_RATES,
  MATERIAL_UNITS,
  MISC_DESCRIPTION_SUGGESTIONS,
  OIL_UNITS,
  PRODUCT_UNITS,
  resolveDiscountAmount,
  resolveLineAmounts,
} from "~/types/purchasing";

import type {
  Currency,
  DiscountType,
  IngredientType,
  OrderType,
  PurchaseOrderFormData,
  PurchaseOrderItemFormData,
} from "~/types/purchasing";

interface PurchaseOrderFormProps {
  orderType: OrderType;
  initialData?: PurchaseOrderFormData;
  onSubmit: (data: PurchaseOrderFormData) => void;
  isSubmitting?: boolean;
  /** Item IDs that have receptions and cannot be deleted */
  protectedItemIds?: Set<string>;
}

const emptyIngredientItem: PurchaseOrderItemFormData = {
  ingredient_type: "oil",
  description: "",
  quantity: 0,
  unit: "kg",
  unit_price: 0,
  discount_type: "none",
  discount_value: 0,
};

// Misceláneos (supplies): free-text line with no catalog reference ("materiales y
// servicios no stock"). ingredient_type "misc" lets reception skip all stock updates.
const emptyMiscItem: PurchaseOrderItemFormData = {
  ingredient_type: "misc",
  description: "",
  quantity: 0,
  unit: "UNI",
  unit_price: 0,
  discount_type: "none",
  discount_value: 0,
  ila_rate: 0,
};

const emptyProductItem: PurchaseOrderItemFormData = {
  ingredient_type: "product",
  description: "",
  quantity: 0,
  unit: "units",
  unit_price: 0,
  discount_type: "none",
  discount_value: 0,
};

export function PurchaseOrderForm({
  orderType,
  initialData,
  onSubmit,
  isSubmitting,
  protectedItemIds = new Set(),
}: PurchaseOrderFormProps) {
  const { data: suppliers = [] } = useActiveSuppliers();
  const { data: companies = [] } = useCompanies();
  const { data: oilTypes = [] } = useOilTypes();
  const { data: rawMaterials = [] } = useRawMaterials();
  const { data: products = [] } = useActiveProducts();
  const { data: miscDescriptions = [] } = useMiscDescriptions();
  const { data: todayRate } = useTodayExchangeRate();

  // Misceláneos combobox options: descriptions used in past misc POs (recency
  // first) merged with the built-in starter suggestions, deduped case-
  // insensitively. UI hints only — picking or creating a value just writes the
  // free-text `description`, so nothing is persisted to a services master.
  const miscOptions = useMemo<SearchableSelectOption[]>(() => {
    const seen = new Set<string>();
    const options: SearchableSelectOption[] = [];
    for (const description of [
      ...miscDescriptions,
      ...MISC_DESCRIPTION_SUGGESTIONS,
    ]) {
      const value = description.trim();
      if (!value) continue;
      const key = value.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      options.push({ value, label: value });
    }
    return options;
  }, [miscDescriptions]);

  // The quote number ("Nº Cotización", e.g. PRISA's CARRO NO.) is stored as the
  // first line of observations. Match the legacy "Nº Contrato:" prefix too so
  // OCs saved before the rename still load their number.
  const [quoteNumber, setQuoteNumber] = useState(
    initialData?.observations?.match(
      /^Nº (?:Cotización|Contrato): (.+?)(?:\r?\n|$)/
    )?.[1] || ""
  );

  // For "supplies" order type: show ALL raw materials (they can purchase any material)
  // The exclude_from_inventory flag controls stock tracking, not purchasing

  const emptyItem =
    orderType === "products"
      ? emptyProductItem
      : orderType === "supplies"
        ? emptyMiscItem
        : emptyIngredientItem;

  const form = useForm<PurchaseOrderFormData>({
    defaultValues: initialData || {
      supplier_id: "",
      company_id: ALIACE_SPA_ID,
      order_type: orderType,
      issue_date: dayjs().format("YYYY-MM-DD"),
      delivery_date: "",
      currency: "CLP",
      payment_terms: "",
      observations: "",
      discount_type: "none",
      discount_value: 0,
      is_exento: false,
      items: [{ ...emptyItem }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "items",
  });

  const currency = form.watch("currency");
  const items = form.watch("items");
  const discountType = (form.watch("discount_type") ?? "none") as DiscountType;
  const discountValue = form.watch("discount_value") ?? 0;
  const isExento = form.watch("is_exento") ?? false;

  // Net line total = gross (quantity * unit_price) minus the per-line discount (cotización Rec/Desc).
  const calculateLineNet = (item: PurchaseOrderItemFormData) =>
    resolveLineAmounts(
      item.quantity,
      item.unit_price,
      item.discount_type,
      item.discount_value
    ).net;

  const calculateTotals = () => {
    // Header subtotal sums the net lines (neto after per-line discounts).
    const subtotal = items.reduce((acc, item) => acc + calculateLineNet(item), 0);
    // Order-level discount comes off the neto BEFORE IVA (the IVA base is the discounted neto).
    const discount = resolveDiscountAmount(subtotal, discountType, discountValue);
    // Exento de IVA: a VAT-exempt cotización adds no 19% IVA.
    const tax = isExento ? 0 : (subtotal - discount) * 0.19;
    // ILA is a per-line additional tax (after IVA); sum each line's rounded amount on its net.
    const ila = items.reduce((acc, item) => {
      return acc + Math.round((calculateLineNet(item) * (item.ila_rate ?? 0)) / 100);
    }, 0);
    const total = subtotal - discount + tax + ila;
    return { subtotal, discount, tax, ila, total };
  };

  const formatCurrency = (value: number) => {
    const symbol = CURRENCY_SYMBOLS[currency as Currency];
    const fractionDigits = currency === "USD" ? 2 : 0;
    return `${symbol}${new Intl.NumberFormat("es-CL", {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(value)}`;
  };

  const totals = calculateTotals();

  const handleSubmitWithQuote = (data: PurchaseOrderFormData) => {
    // The observations field is seeded with the full stored text, which already
    // includes the "Nº Cotización: …" line extracted into `quoteNumber` (older
    // OCs used "Nº Contrato:"). Strip that leading line before re-prepending so
    // re-saving stays idempotent and doesn't duplicate the quote number.
    const baseObservations = (data.observations || "").replace(
      /^Nº (?:Cotización|Contrato): .*(?:\r?\n|$)/,
      ""
    );
    const finalObservations = quoteNumber
      ? `Nº Cotización: ${quoteNumber}${baseObservations ? `\n${baseObservations}` : ""}`
      : baseObservations || undefined;

    onSubmit({ ...data, observations: finalObservations });
  };

  const handleIngredientTypeChange = (index: number, type: IngredientType) => {
    form.setValue(`items.${index}.ingredient_type`, type);
    form.setValue(`items.${index}.oil_type_id`, undefined);
    form.setValue(`items.${index}.raw_material_id`, undefined);
    form.setValue(`items.${index}.description`, "");
    form.setValue(`items.${index}.unit`, type === "oil" ? "kg" : "units");
  };

  const handleIngredientSelect = (
    index: number,
    id: string,
    type: IngredientType
  ) => {
    if (type === "oil") {
      const oilType = oilTypes.find((o) => o.id === id);
      form.setValue(`items.${index}.oil_type_id`, id);
      form.setValue(`items.${index}.description`, oilType?.name || "");
    } else {
      const material = rawMaterials.find((m) => m.id === id);
      form.setValue(`items.${index}.raw_material_id`, id);
      form.setValue(`items.${index}.description`, material?.name || "");
      form.setValue(`items.${index}.unit`, material?.unit || "units");
    }
  };

  const handleProductSelect = (index: number, id: string) => {
    const product = products.find((p) => p.id === id);
    form.setValue(`items.${index}.product_id`, id);
    form.setValue(`items.${index}.description`, product?.short_name || "");
    if (product?.net_price) {
      form.setValue(`items.${index}.unit_price`, product.net_price);
    }
  };

  // Misceláneos: free-text line name. Any edited/new supplies item becomes a "misc"
  // item with no catalog reference, so it never touches stock on reception.
  const handleMiscDescriptionChange = (index: number, value: string) => {
    form.setValue(`items.${index}.description`, value);
    form.setValue(`items.${index}.ingredient_type`, "misc");
    form.setValue(`items.${index}.raw_material_id`, undefined);
    form.setValue(`items.${index}.oil_type_id`, undefined);
    form.setValue(`items.${index}.product_id`, undefined);
  };

  const getUnitsForType = (type: IngredientType | "product") => {
    if (type === "product") return PRODUCT_UNITS;
    return type === "oil" ? OIL_UNITS : MATERIAL_UNITS;
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmitWithQuote)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Información General</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <FormField
              control={form.control}
              name="company_id"
              rules={{ required: "Selecciona una empresa" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Empresa *</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value ?? ALIACE_SPA_ID}
                  >
                    <FormControl>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Seleccionar empresa" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="supplier_id"
              rules={{ required: "Selecciona un proveedor" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Proveedor *</FormLabel>
                  <FormControl>
                    <SearchableSelect
                      value={field.value}
                      onValueChange={field.onChange}
                      options={suppliers.map((s) => ({
                        value: s.id,
                        label: s.name,
                        searchTerms: `${s.name} ${s.rut || ""}`,
                      }))}
                      placeholder="Seleccionar proveedor"
                      searchPlaceholder="Buscar proveedor..."
                      emptyMessage="No se encontraron proveedores"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="issue_date"
              rules={{ required: "Fecha requerida" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fecha de Emisión *</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="delivery_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fecha de Entrega</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="currency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Moneda</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.entries(CURRENCY_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="payment_terms"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Condiciones de Pago</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Ej: 30 días"
                      onFocus={(e) => e.target.select()}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormItem>
              <FormLabel>Nº Cotización</FormLabel>
              <FormControl>
                <Input
                  placeholder="Ej: 1272296"
                  value={quoteNumber}
                  onChange={(e) => setQuoteNumber(e.target.value)}
                  onFocus={(e) => e.target.select()}
                />
              </FormControl>
            </FormItem>

            {currency === "USD" && todayRate && (
              <div className="col-span-full text-sm text-muted-foreground">
                Tipo de cambio del día: ${todayRate.usd_clp.toFixed(2)} CLP/USD
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>
              {orderType === "products"
                ? "Productos"
                : orderType === "supplies"
                  ? "Misceláneos"
                  : "Items"}
            </CardTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => append({ ...emptyItem })}
            >
              <Plus className="mr-2 h-4 w-4" />
              Agregar{" "}
              {orderType === "products"
                ? "Producto"
                : orderType === "supplies"
                  ? "Misceláneo"
                  : "Item"}
            </Button>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {orderType === "ingredients" && (
                      <TableHead className="w-[120px]">Tipo</TableHead>
                    )}
                    <TableHead className="w-[320px]">
                      {orderType === "products"
                        ? "Producto"
                        : orderType === "supplies"
                          ? "Misceláneo"
                          : "Ingrediente"}
                    </TableHead>
                    <TableHead className="w-[100px]">Cantidad</TableHead>
                    <TableHead className="w-[100px]">Unidad</TableHead>
                    <TableHead className="w-[120px]">Precio Unit.</TableHead>
                    <TableHead className="w-[150px]">Descuento</TableHead>
                    {orderType === "supplies" && (
                      <TableHead className="w-[90px]">ILA</TableHead>
                    )}
                    <TableHead className="w-[120px] text-right">
                      Subtotal
                    </TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fields.map((field, index) => {
                    const item = items[index];
                    const itemDiscountType = (item?.discount_type ??
                      "none") as DiscountType;
                    const itemSubtotal = item
                      ? calculateLineNet(item)
                      : 0;

                    return (
                      <TableRow key={field.id}>
                        {orderType === "ingredients" && (
                          <TableCell>
                            <Select
                              value={item?.ingredient_type || "oil"}
                              onValueChange={(value) =>
                                handleIngredientTypeChange(
                                  index,
                                  value as IngredientType
                                )
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="oil">Aceite</SelectItem>
                                <SelectItem value="raw_material">
                                  Material
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                        )}
                        <TableCell>
                          {orderType === "products" ? (
                            <SearchableSelect
                              value={item?.product_id || ""}
                              onValueChange={(value) =>
                                handleProductSelect(index, value)
                              }
                              options={products.map((p) => ({
                                value: p.id,
                                label: `${p.short_name}${p.barcode ? ` (${p.barcode})` : ""}`,
                                searchTerms: `${p.short_name} ${p.barcode || ""}`,
                              }))}
                              placeholder="Seleccionar producto"
                              searchPlaceholder="Buscar producto..."
                              emptyMessage="No se encontraron productos"
                            />
                          ) : orderType === "supplies" ? (
                            <SearchableSelect
                              allowCreate
                              value={item?.description ?? ""}
                              onValueChange={(value) =>
                                handleMiscDescriptionChange(index, value)
                              }
                              onCreate={(value) =>
                                handleMiscDescriptionChange(index, value)
                              }
                              options={miscOptions}
                              placeholder="Seleccionar o escribir"
                              searchPlaceholder="Buscar o escribir…"
                              emptyMessage="Escribe para agregar uno nuevo"
                            />
                          ) : item?.ingredient_type === "raw_material" ? (
                            <SearchableSelect
                              value={item?.raw_material_id || ""}
                              onValueChange={(value) =>
                                handleIngredientSelect(index, value, "raw_material")
                              }
                              options={rawMaterials.map((material) => ({
                                value: material.id,
                                label: `${material.name} (${material.code})`,
                                searchTerms: `${material.name} ${material.code}`,
                              }))}
                              placeholder="Seleccionar material"
                              searchPlaceholder="Buscar material..."
                              emptyMessage="No se encontraron materiales"
                            />
                          ) : (
                            <SearchableSelect
                              value={item?.oil_type_id || ""}
                              onValueChange={(value) =>
                                handleIngredientSelect(index, value, "oil")
                              }
                              options={oilTypes.map((oil) => ({
                                value: oil.id,
                                label: `${oil.name} (${oil.code})`,
                                searchTerms: `${oil.name} ${oil.code}`,
                              }))}
                              placeholder="Seleccionar aceite"
                              searchPlaceholder="Buscar aceite..."
                              emptyMessage="No se encontraron aceites"
                            />
                          )}
                        </TableCell>

                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            onFocus={(e) => e.target.select()}
                            {...form.register(`items.${index}.quantity`, {
                              valueAsNumber: true,
                            })}
                          />
                        </TableCell>

                        <TableCell>
                          <Select
                            value={item?.unit || ""}
                            onValueChange={(value) =>
                              form.setValue(`items.${index}.unit`, value)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {getUnitsForType(
                                orderType === "products"
                                  ? "product"
                                  : orderType === "supplies"
                                    ? "raw_material"
                                    : (item?.ingredient_type as IngredientType) ||
                                      "oil"
                              ).map((unit) => (
                                <SelectItem key={unit.value} value={unit.value}>
                                  {unit.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>

                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            onFocus={(e) => e.target.select()}
                            {...form.register(`items.${index}.unit_price`, {
                              valueAsNumber: true,
                            })}
                          />
                        </TableCell>

                        {/* Per-line discount (cotización "Rec/Desc"): type + value */}
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Select
                              value={itemDiscountType}
                              onValueChange={(value) => {
                                form.setValue(
                                  `items.${index}.discount_type`,
                                  value as DiscountType
                                );
                                if (value === "none") {
                                  form.setValue(
                                    `items.${index}.discount_value`,
                                    0
                                  );
                                }
                              }}
                            >
                              <SelectTrigger className="h-9 w-[62px] px-2">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">—</SelectItem>
                                <SelectItem value="percentage">%</SelectItem>
                                <SelectItem value="amount">$</SelectItem>
                              </SelectContent>
                            </Select>
                            {itemDiscountType !== "none" && (
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                className="h-9 w-[76px] text-right"
                                onFocus={(e) => e.target.select()}
                                {...form.register(
                                  `items.${index}.discount_value`,
                                  { valueAsNumber: true }
                                )}
                              />
                            )}
                          </div>
                        </TableCell>

                        {orderType === "supplies" && (
                          <TableCell>
                            <Select
                              value={String(item?.ila_rate ?? 0)}
                              onValueChange={(value) =>
                                form.setValue(
                                  `items.${index}.ila_rate`,
                                  Number(value)
                                )
                              }
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ILA_RATES.map((rate) => (
                                  <SelectItem key={rate} value={String(rate)}>
                                    {formatIlaRate(rate)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                        )}

                        <TableCell className="text-right font-mono">
                          {formatCurrency(itemSubtotal)}
                        </TableCell>

                        <TableCell>
                          {fields.length > 1 &&
                            (item?.id && protectedItemIds.has(item.id) ? (
                              <div
                                className="flex h-9 w-9 items-center justify-center"
                                title="Este item tiene recepciones y no puede ser eliminado"
                              >
                                <Lock className="h-4 w-4 text-muted-foreground" />
                              </div>
                            ) : (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => remove(index)}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            ))}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <div className="mt-4 flex justify-end">
              <div className="w-80 space-y-2">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span className="font-mono">
                    {formatCurrency(totals.subtotal)}
                  </span>
                </div>

                {/* Order-level discount, applied to the neto before IVA */}
                <div className="flex items-center justify-between gap-2">
                  <span className="whitespace-nowrap">Descuento:</span>
                  <div className="flex items-center gap-2">
                    <Select
                      value={discountType}
                      onValueChange={(value) => {
                        form.setValue(
                          "discount_type",
                          value as DiscountType
                        );
                        if (value === "none") {
                          form.setValue("discount_value", 0);
                        }
                      }}
                    >
                      <SelectTrigger className="h-9 w-[132px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(
                          Object.entries(DISCOUNT_TYPE_LABELS) as [
                            DiscountType,
                            string,
                          ][]
                        ).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {discountType !== "none" && (
                      <div className="relative">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          className="h-9 w-[92px] pr-6 text-right"
                          onFocus={(e) => e.target.select()}
                          {...form.register("discount_value", {
                            valueAsNumber: true,
                          })}
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                          {discountType === "percentage" ? "%" : "$"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                {totals.discount > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>
                      {discountType === "percentage"
                        ? `Descuento (${formatIlaRate(discountValue)})`
                        : "Descuento aplicado"}
                    </span>
                    <span className="font-mono">
                      -{formatCurrency(totals.discount)}
                    </span>
                  </div>
                )}

                {/* Exenta de IVA: when the cotización is VAT-exempt the OC adds no 19% IVA */}
                <label className="flex cursor-pointer items-center justify-between gap-2">
                  <span>Exenta de IVA</span>
                  <Checkbox
                    checked={isExento}
                    onCheckedChange={(checked) =>
                      form.setValue("is_exento", checked === true)
                    }
                  />
                </label>

                <div className="flex justify-between">
                  <span>{isExento ? "IVA (exenta)" : "IVA (19%)"}:</span>
                  <span className="font-mono">
                    {formatCurrency(totals.tax)}
                  </span>
                </div>
                {totals.ila > 0 && (
                  <div className="flex justify-between">
                    <span>ILA:</span>
                    <span className="font-mono">
                      {formatCurrency(totals.ila)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between border-t pt-2 font-bold">
                  <span>Total:</span>
                  <span className="font-mono">
                    {formatCurrency(totals.total)}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Observaciones</CardTitle>
          </CardHeader>
          <CardContent>
            <FormField
              control={form.control}
              name="observations"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Textarea
                      placeholder="Notas adicionales para la orden de compra..."
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Guardando..." : "Guardar Orden"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
